<template>
  <!--
    App / info side drawer. Slides in from the LEFT when the header Syntry logo
    is tapped (see AppHeader → menuController.open('app-menu')). Companion to the
    right-side account drawer (AppSideMenu). Swipe-to-open is disabled so it's
    only reachable from authenticated screens via the logo — never by an edge
    swipe on sign-in / onboarding, which share the same root router outlet.
  -->
  <ion-menu
    menu-id="app-menu"
    content-id="main-content"
    side="start"
    type="overlay"
    :swipe-gesture="false"
    class="app-menu"
  >
    <ion-content class="app-menu__content">
      <div class="app-menu__inner">
        <!-- ─── Brand header ──────────────────────────────────────────── -->
        <header class="app-menu__head">
          <img src="/assets/logo/syntry-mark.svg" alt="" class="app-menu__mark" aria-hidden="true" />
          <div class="app-menu__brand">
            <p class="app-menu__name">{{ appName }}</p>
            <p class="app-menu__powered">Powered by <strong>ERPat</strong></p>
          </div>
          <span class="app-menu__version-chip">v{{ appVersion }}</span>
        </header>

        <!-- ─── Grouped links ─────────────────────────────────────────── -->
        <nav class="app-menu__nav" aria-label="App menu">
          <template v-for="group in groups" :key="group.title">
            <p class="app-menu__group-title">{{ group.title }}</p>
            <button
              v-for="item in group.items"
              :key="item.id"
              type="button"
              class="app-menu__item"
              :aria-label="item.external ? `${item.label} (opens in browser)` : item.label"
              @click="onItem(item)"
            >
              <span class="app-menu__item-icon-wrap" aria-hidden="true">
                <ion-icon :icon="item.icon" class="app-menu__item-icon" />
              </span>
              <span class="app-menu__item-label">{{ item.label }}</span>
              <ion-icon
                :icon="item.external ? openOutline : chevronForwardOutline"
                class="app-menu__item-chevron"
                aria-hidden="true"
              />
            </button>
          </template>
        </nav>

        <!-- ─── Version + copyright footer ────────────────────────────── -->
        <footer class="app-menu__footer">
          <div class="app-menu__version-card" role="list" aria-label="Version details">
            <div class="app-menu__version-row" role="listitem">
              <span class="app-menu__version-label">Version</span>
              <span class="app-menu__version-value">{{ appVersion }}</span>
            </div>
            <div class="app-menu__version-row" role="listitem">
              <span class="app-menu__version-label">Build</span>
              <span class="app-menu__version-value">{{ appBuild }}</span>
            </div>
            <div class="app-menu__version-row" role="listitem">
              <span class="app-menu__version-label">Released</span>
              <span class="app-menu__version-value">{{ appReleased }}</span>
            </div>
          </div>
          <p class="app-menu__copyright">© 2026 ERPat Systems. All rights reserved.</p>
        </footer>
      </div>
    </ion-content>

    <!-- ─── Rate this App modal (opened from the App group) ──────────── -->
    <ion-modal
      :is-open="showRate"
      css-class="app-menu-rate-modal"
      :backdrop-dismiss="!ratingSubmitting"
      @did-dismiss="showRate = false"
    >
      <RateAppModal
        :pending="ratingSubmitting"
        @confirm="onRatingConfirm"
        @cancel="showRate = false"
      />
    </ion-modal>

    <!-- ─── Install app modal (opened from the App group) ─────────────── -->
    <ion-modal
      :is-open="showInstall"
      css-class="install-modal"
      :backdrop-dismiss="!installPending"
      @did-dismiss="showInstall = false"
    >
      <InstallAppModal
        :mode="installMode"
        :pending="installPending"
        @install="onInstall"
        @cancel="showInstall = false"
      />
    </ion-modal>
  </ion-menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IonMenu, IonContent, IonIcon, IonModal, menuController } from '@ionic/vue';
import {
  informationCircleOutline,
  starOutline,
  helpCircleOutline,
  globeOutline,
  headsetOutline,
  documentTextOutline,
  shieldCheckmarkOutline,
  chevronForwardOutline,
  downloadOutline,
  openOutline,
} from 'ionicons/icons';
import InstallAppModal from '@/components/modals/InstallAppModal.vue';
import { installMode, promptInstall, showInstallEntry } from '@/pwa/install';
import {
  APP_NAME,
  APP_VERSION,
  APP_BUILD,
  APP_RELEASE_DATE,
} from '@/utils/app-info';
import RateAppModal from '@/components/modals/RateAppModal.vue';
import type { RatingPayload } from '@/components/modals/RateAppModal.vue';
import { appRatingService } from '@/services/erpat';
import { useToast } from '@/composables/useToast';
import { errorMessage } from '@/services/api/http';

const router = useRouter();

const appName = APP_NAME;
const appVersion = APP_VERSION;
const appBuild = APP_BUILD;
const appReleased = APP_RELEASE_DATE;

// Rate-this-app modal (moved here from the About page) — opens over the app
// once the drawer closes, mirroring the Language picker pattern in AppSideMenu.
const showRate = ref(false);
const ratingSubmitting = ref(false);

// Install-this-app modal — same nesting pattern as the rating modal above.
const showInstall = ref(false);
const installPending = ref(false);

interface AppMenuItem {
  id: string;
  label: string;
  icon: string;
  /** Internal route to push… */
  route?: string;
  /** …or external URL opened in the browser. */
  url?: string;
  external?: boolean;
}
interface AppMenuGroup {
  title: string;
  items: AppMenuItem[];
}

// All items navigate to in-app routes (router/index.ts). "Visit erpat.app"
// renders the website inside the app via WebsitePage (an embedded frame),
// matching the Help Center; the external-link branch in onItem is retained for
// any future external entries.
// A computed rather than a constant so the install entry can appear/disappear
// with `showInstallEntry` (which is false in the native shell, once the app is
// already installed, and in browsers that offer no install route at all).
const groups = computed<AppMenuGroup[]>(() => [
  {
    title: 'App',
    items: [
      { id: 'about', label: 'About App', icon: informationCircleOutline, route: '/about' },
      ...(showInstallEntry.value
        ? [
            {
              id: 'install',
              label: 'Install app',
              icon: downloadOutline,
            },
          ]
        : []),
      { id: 'rate', label: 'Rate this App', icon: starOutline },
      { id: 'website', label: 'Visit erpat.app', icon: globeOutline, route: '/website' },
    ],
  },
  {
    title: 'Support',
    items: [
      { id: 'contact', label: 'Contact Support', icon: headsetOutline, route: '/support' },
      { id: 'help', label: 'Help Center', icon: helpCircleOutline, route: '/help' },
    ],
  },
  {
    title: 'Legal',
    items: [
      { id: 'terms', label: 'Terms of Service', icon: documentTextOutline, route: '/terms' },
      { id: 'privacy', label: 'Privacy Policy', icon: shieldCheckmarkOutline, route: '/privacy' },
    ],
  },
]);

/** Close the drawer first (awaiting the animation), then navigate / open. */
async function onItem(item: AppMenuItem): Promise<void> {
  await menuController.close('app-menu');
  if (item.id === 'rate') {
    showRate.value = true;
    return;
  }
  if (item.id === 'install') {
    showInstall.value = true;
    return;
  }
  if (item.external && item.url) {
    window.open(item.url, '_blank', 'noopener');
    return;
  }
  if (item.route) void router.push(item.route);
}

const toast = useToast();

/**
 * These fire from inside the open side drawer, which owns the bottom of the
 * screen — a bottom toast would land behind it. Top, for this menu only.
 */
const TOP = { position: 'top' } as const;

async function onInstall(): Promise<void> {
  if (installPending.value) return;
  installPending.value = true;
  try {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      await toast.success('Installing Syntry — check your home screen.', TOP);
    } else if (outcome === 'unavailable') {
      await toast.error("Installation isn't available in this browser.", TOP);
    }
  } finally {
    installPending.value = false;
    showInstall.value = false;
  }
}

async function onRatingConfirm(payload: RatingPayload): Promise<void> {
  if (ratingSubmitting.value) return;
  if (payload.rating < 1) {
    await toast.warning('Please choose a star rating first.', TOP);
    return;
  }
  ratingSubmitting.value = true;
  try {
    await appRatingService.submit(payload.rating, payload.review);
    showRate.value = false;
    await toast.success('Thanks for rating Syntry!', TOP);
  } catch (err) {
    await toast.error(errorMessage(err), TOP);
  } finally {
    ratingSubmitting.value = false;
  }
}
</script>

<!-- Non-scoped: must be global so the Ionic modal portal receives these styles -->
<style lang="scss">
ion-modal.app-menu-rate-modal {
  /* Transparent content so the frosted-glass card shows through. */
  --background: transparent;
  --width: 90%;
  --max-width: 420px;
  --height: auto;
  --max-height: 90vh;
  --border-radius: var(--syntry-radius-modal);
  --box-shadow: none;
  /* Light backdrop so the blurred page stays visible behind the glass. */
  --backdrop-opacity: 0.32;
  /* Vertically (and horizontally) centre the dialog. */
  align-items: center;
  justify-content: center;
}

ion-modal.app-menu-rate-modal .ion-page {
  position: relative;
  contain: content;
  background: transparent;
  height: auto;
}
</style>

<style scoped lang="scss">
.app-menu {
  --width: 84%;
  --max-width: 360px;

  &__content {
    --background: var(--syntry-bg);
  }

  // Flex column so the footer settles at the bottom on short content.
  &__inner {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  /* ── Brand header ──────────────────────────────────────────────── */
  &__head {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    padding: calc(var(--syntry-safe-top, env(safe-area-inset-top, 0px)) + var(--sp-4)) var(--sp-4) var(--sp-4);
    background: var(--syntry-surface);
    border-bottom: 1px solid var(--syntry-border);
  }

  &__mark {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  &__brand {
    flex: 1;
    min-width: 0;
  }

  /* --syntry-mark, not --syntry-primary: the Syntry wordmark is product
     identity and must stay teal whatever brand color the tenant picks
     (see src/theme/brand-color.ts — the mark tokens are never overridden). */
  &__name {
    margin: 0;
    font-size: var(--syntry-fs-body);
    font-weight: 800;
    color: var(--syntry-mark);
    letter-spacing: -0.01em;
    line-height: 1.1;
  }

  &__powered {
    margin: 3px 0 0;
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-muted);

    strong { color: var(--erpat-primary); font-weight: 700; }
  }

  /* Fixed mark tokens: the version chip belongs to the brand header, so it
     stays teal alongside the wordmark under any tenant brand color. */
  &__version-chip {
    flex-shrink: 0;
    padding: 3px var(--sp-2);
    border-radius: var(--syntry-radius-chip);
    background: var(--syntry-mark-soft);
    color: var(--syntry-mark);
    font-size: var(--syntry-fs-tiny);
    font-weight: 700;
  }

  /* ── Grouped links ─────────────────────────────────────────────── */
  &__nav {
    padding: var(--sp-3) var(--sp-3) 0;
  }

  &__group-title {
    margin: var(--sp-3) 0 var(--sp-1);
    padding: 0 var(--sp-3);
    font-size: var(--syntry-fs-tiny);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--syntry-text-soft);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    padding: var(--sp-3);
    min-height: 52px;
    background: none;
    border: none;
    border-radius: var(--syntry-radius-md);
    cursor: pointer;
    text-align: left;
    -webkit-tap-highlight-color: transparent;

    &:active { background: var(--syntry-bg-soft); }
  }

  /* Every tile uses the fixed --syntry-mark tokens: the drawer is product
     chrome, so its icons stay Syntry teal and never follow the tenant brand
     color, which overrides --syntry-teal-soft / --syntry-primary
     (see src/theme/brand-color.ts). */
  &__item-icon-wrap {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: var(--syntry-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--syntry-mark-soft);
  }

  &__item-icon {
    font-size: 19px;
    color: var(--syntry-mark);
  }

  &__item-label {
    flex: 1;
    min-width: 0;
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-strong);
  }

  &__item-chevron {
    font-size: 18px;
    color: var(--syntry-text-soft);
    flex-shrink: 0;
  }

  /* ── Footer (version + copyright) ──────────────────────────────── */
  &__footer {
    margin-top: auto;
    padding: var(--sp-4);
  }

  &__version-card {
    background: var(--syntry-surface);
    border-radius: var(--syntry-radius-card);
    box-shadow: var(--syntry-shadow-card);
    overflow: hidden;
  }

  &__version-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-3);
    padding: var(--sp-3) var(--sp-4);

    & + & {
      border-top: 1px solid var(--syntry-border);
    }
  }

  &__version-label {
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-muted);
  }

  &__version-value {
    font-size: var(--syntry-fs-tiny);
    font-weight: 600;
    color: var(--syntry-text-strong);
  }

  &__copyright {
    margin: var(--sp-4) 0 0;
    text-align: center;
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-soft);
  }
}
</style>
